import { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { PaperPlaneRight, ChatCircle, MagnifyingGlass, Paperclip, X, UserPlus, Buildings, ArrowLeft, DownloadSimple } from "@phosphor-icons/react";
import UserAvatar from "@/components/UserAvatar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const roleColor = (r) => {
  if (r === "admin") return "text-[#CCFF00]";
  if (r === "directivo") return "text-blue-400";
  if (r === "profesor") return "text-yellow-400";
  return "text-gray-300";
};

const fmtTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const today = new Date();
  const isToday = d.toDateString() === today.toDateString();
  if (isToday) return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  return d.toLocaleDateString([], { day: "2-digit", month: "2-digit" }) + " " + d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

export default function Chat() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [conversations, setConversations] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [contactsOpen, setContactsOpen] = useState(false);
  const scrollRef = useRef(null);

  const loadConversations = useCallback(async () => {
    try {
      const { data } = await api.get("/chat/conversations");
      setConversations(data || []);
      // Auto-open ?to=<user_id> or ?conv=<cid>
      const to = searchParams.get("to");
      const convId = searchParams.get("conv");
      if (to && !activeConv) {
        const { data: c } = await api.post("/chat/conversations", { user_id: to });
        setActiveConv(c);
        // Remove ?to= from URL to prevent reopening
        searchParams.delete("to");
        setSearchParams(searchParams, { replace: true });
      } else if (convId && !activeConv) {
        const found = (data || []).find((c) => c.id === convId);
        if (found) setActiveConv(found);
      }
    } catch {
      toast.error("No se pudieron cargar las conversaciones");
    }
  }, [searchParams, activeConv, setSearchParams]);

  useEffect(() => { loadConversations(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll for updates every 6s
  useEffect(() => {
    const tm = setInterval(() => loadConversations(), 6000);
    return () => clearInterval(tm);
  }, [loadConversations]);

  const loadMessages = useCallback(async (conv) => {
    if (!conv) return;
    try {
      const { data } = await api.get(`/chat/conversations/${conv.id}/messages?limit=100`);
      setMessages(data.messages || []);
      await api.post(`/chat/conversations/${conv.id}/read`);
      // Reload sidebar unread counts
      loadConversations();
      // Scroll to bottom
      setTimeout(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, 50);
    } catch {}
  }, [loadConversations]);

  useEffect(() => { if (activeConv) loadMessages(activeConv); }, [activeConv?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll active conversation messages every 5s
  useEffect(() => {
    if (!activeConv) return;
    const tm = setInterval(() => loadMessages(activeConv), 5000);
    return () => clearInterval(tm);
  }, [activeConv, loadMessages]);

  const send = async () => {
    if (!activeConv) return;
    if (!text.trim() && files.length === 0) return;
    setSending(true);
    try {
      let resp;
      if (files.length > 0) {
        const fd = new FormData();
        fd.append("text", text.trim());
        files.forEach((f) => fd.append("files", f));
        resp = await api.post(`/chat/conversations/${activeConv.id}/messages`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        resp = await api.post(`/chat/conversations/${activeConv.id}/messages`, { text: text.trim() });
      }
      setMessages((m) => [...m, resp.data]);
      setText(""); setFiles([]);
      loadConversations();
      setTimeout(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, 50);
    } catch (e) { toast.error(e.response?.data?.detail || "Error al enviar"); }
    finally { setSending(false); }
  };

  const startNewChat = async (targetId) => {
    try {
      const { data } = await api.post("/chat/conversations", { user_id: targetId });
      setActiveConv(data);
      setContactsOpen(false);
      loadConversations();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <div className="flex flex-col h-full" data-testid="chat-page">
      <div className="flex items-center gap-3 mb-3">
        <ChatCircle size={30} weight="fill" className="text-[#CCFF00]" />
        <div className="flex-1">
          <h1 className="text-2xl md:text-3xl font-['Barlow_Condensed'] font-black uppercase tracking-wider text-white">Chat privado</h1>
          <p className="text-xs text-gray-400">Mensajes 1-a-1 con cualquier usuario o directivo del club</p>
        </div>
        <Button onClick={() => setContactsOpen(true)} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold" data-testid="chat-new-btn">
          <UserPlus size={14} className="mr-1" /> Nuevo chat
        </Button>
      </div>

      <div className="flex flex-1 gap-3 min-h-[600px] max-h-[calc(100vh-200px)]">
        {/* Sidebar */}
        <aside className={`w-full sm:w-72 bg-white/5 border border-white/10 rounded-lg overflow-hidden flex flex-col ${activeConv ? "hidden sm:flex" : "flex"}`} data-testid="chat-sidebar">
          <div className="p-3 border-b border-white/10">
            <p className="text-[10px] uppercase tracking-widest text-gray-400">Conversaciones ({conversations.length})</p>
          </div>
          <ul className="flex-1 overflow-y-auto">
            {conversations.length === 0 && <li className="p-4 text-center text-xs text-gray-500">Sin conversaciones. Abre un chat nuevo →</li>}
            {conversations.map((c) => (
              <li key={c.id}>
                <button onClick={() => setActiveConv(c)} className={`w-full text-left p-3 border-b border-white/5 hover:bg-white/5 ${activeConv?.id === c.id ? "bg-[#CCFF00]/10 border-l-2 border-l-[#CCFF00]" : ""}`} data-testid={`chat-conv-${c.id}`}>
                  <div className="flex items-center gap-2">
                    <UserAvatar src={c.other?.profile_photo_url} fallback={(c.other?.name || "?").slice(0,2)} className="w-9 h-9 rounded-full" />
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-bold truncate ${roleColor(c.other?.role)}`}>{c.other?.name || "Usuario"}</p>
                      <p className="text-[11px] text-gray-500 truncate">{c.last_message_preview || "Sin mensajes aún"}</p>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <span className="text-[9px] text-gray-500">{fmtTime(c.last_message_at)}</span>
                      {c.unread_count > 0 && <Badge className="bg-[#CCFF00] text-black text-[10px] px-1.5 py-0" data-testid={`chat-unread-${c.id}`}>{c.unread_count}</Badge>}
                    </div>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </aside>

        {/* Chat window */}
        <main className={`flex-1 bg-white/5 border border-white/10 rounded-lg flex flex-col overflow-hidden ${activeConv ? "flex" : "hidden sm:flex"}`} data-testid="chat-window">
          {!activeConv ? (
            <div className="flex-1 flex items-center justify-center text-gray-500 text-sm">
              Selecciona una conversación o inicia una nueva
            </div>
          ) : (
            <>
              <header className="p-3 border-b border-white/10 flex items-center gap-2">
                <button onClick={() => setActiveConv(null)} className="sm:hidden text-gray-400"><ArrowLeft size={18} /></button>
                <UserAvatar src={activeConv.other?.profile_photo_url} fallback={(activeConv.other?.name || "?").slice(0,2)} className="w-8 h-8 rounded-full" />
                <div>
                  <p className={`font-bold text-sm ${roleColor(activeConv.other?.role)}`} data-testid="chat-header-name">{activeConv.other?.name || "Usuario"}</p>
                  <p className="text-[10px] uppercase tracking-wider text-gray-500">{activeConv.other?.role || ""}</p>
                </div>
              </header>

              <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-2 bg-black/20" data-testid="chat-messages">
                {messages.length === 0 && <p className="text-center text-xs text-gray-500 pt-6">Envía el primer mensaje 👋</p>}
                {messages.map((m) => {
                  const mine = m.sender_id === user.id;
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${m.id}`}>
                      <div className={`max-w-[75%] rounded-lg p-2.5 ${mine ? "bg-[#CCFF00] text-black" : "bg-white/10 text-white"}`}>
                        {m.attachments?.map((att) => (
                          <ChatAttachment key={att.key} att={att} mine={mine} />
                        ))}
                        {m.text && <p className="text-sm whitespace-pre-wrap break-words">{m.text}</p>}
                        <p className={`text-[9px] mt-1 opacity-70 text-right ${mine ? "text-black/70" : "text-gray-400"}`}>{fmtTime(m.created_at)}</p>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="p-3 border-t border-white/10 space-y-2">
                {files.length > 0 && (
                  <ul className="flex flex-wrap gap-1" data-testid="chat-files-preview">
                    {files.map((f, i) => (
                      <li key={i} className="flex items-center gap-1 bg-white/10 text-white text-[11px] px-2 py-1 rounded">
                        <Paperclip size={12} /> {f.name.slice(0, 24)}
                        <button onClick={() => setFiles((fs) => fs.filter((_, j) => j !== i))} className="text-red-300"><X size={12} /></button>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="flex items-center gap-2">
                  <label className="cursor-pointer text-gray-400 hover:text-[#CCFF00]" data-testid="chat-attach-label">
                    <Paperclip size={20} />
                    <input type="file" multiple onChange={(e) => setFiles([...files, ...Array.from(e.target.files || [])])} className="hidden" data-testid="chat-attach-input" />
                  </label>
                  <Input value={text} onChange={(e) => setText(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                    placeholder="Escribe un mensaje…" className="bg-black/50 border-white/10 text-white flex-1" data-testid="chat-input" />
                  <Button onClick={send} disabled={sending || (!text.trim() && files.length === 0)} className="bg-[#CCFF00] text-black font-bold" data-testid="chat-send-btn">
                    <PaperPlaneRight size={16} weight="fill" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </main>
      </div>

      <ContactsDialog open={contactsOpen} onClose={() => setContactsOpen(false)} onPick={startNewChat} userClubId={user?.club_id} />
    </div>
  );
}

function ChatAttachment({ att, mine }) {
  const url = `${BACKEND_URL}/api/chat/attachment/${encodeURIComponent(att.key)}`;
  const token = localStorage.getItem("padel_token");
  const [blobUrl, setBlobUrl] = useState(null);
  useEffect(() => {
    let cancelled = false;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob()).then((b) => { if (!cancelled) setBlobUrl(URL.createObjectURL(b)); })
      .catch(() => {});
    return () => { cancelled = true; if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [url]); // eslint-disable-line react-hooks/exhaustive-deps
  if (att.is_image && blobUrl) {
    return <img src={blobUrl} alt={att.filename} className="rounded mb-1 max-w-full max-h-64 border border-black/20" />;
  }
  return (
    <a href={blobUrl || "#"} target="_blank" rel="noreferrer" download={att.filename}
       className={`flex items-center gap-2 mb-1 p-2 rounded border ${mine ? "border-black/20 bg-black/5" : "border-white/20 bg-black/30"}`}>
      <DownloadSimple size={16} weight="bold" />
      <span className="text-xs font-mono truncate max-w-[200px]">{att.filename}</span>
      <span className="text-[9px] opacity-70">{Math.round((att.size || 0) / 1024)}KB</span>
    </a>
  );
}

function ContactsDialog({ open, onClose, onPick, userClubId }) {
  const [q, setQ] = useState("");
  const [users, setUsers] = useState([]);
  const [directivos, setDirectivos] = useState([]);
  const [tab, setTab] = useState("users"); // users | club

  useEffect(() => {
    if (!open) return;
    const tm = setTimeout(() => {
      api.get(`/chat/contacts?q=${encodeURIComponent(q)}`).then(({ data }) => setUsers(data)).catch(() => {});
    }, 250);
    return () => clearTimeout(tm);
  }, [q, open]);

  useEffect(() => {
    if (!open || !userClubId) return;
    api.get(`/chat/clubs/${userClubId}/directivos`).then(({ data }) => setDirectivos(data)).catch(() => {});
  }, [open, userClubId]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md bg-black border-white/10 text-white" data-testid="chat-contacts-dialog">
        <DialogHeader><DialogTitle className="text-white uppercase font-['Barlow_Condensed']">Iniciar chat</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="flex gap-1" data-testid="chat-contacts-tabs">
            <button onClick={() => setTab("users")} className={`flex-1 py-2 text-xs uppercase font-bold rounded ${tab === "users" ? "bg-[#CCFF00] text-black" : "bg-white/5 text-gray-300"}`} data-testid="chat-tab-users">Usuarios</button>
            <button onClick={() => setTab("club")} className={`flex-1 py-2 text-xs uppercase font-bold rounded ${tab === "club" ? "bg-[#CCFF00] text-black" : "bg-white/5 text-gray-300"}`} data-testid="chat-tab-club">Directivos del club</button>
          </div>
          {tab === "users" && (
            <>
              <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded px-2">
                <MagnifyingGlass size={14} className="text-gray-400" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre o email…" className="bg-transparent border-0 text-white" data-testid="chat-contacts-search" />
              </div>
              <ul className="max-h-72 overflow-y-auto space-y-1">
                {users.map((u) => (
                  <li key={u.id}>
                    <button onClick={() => onPick(u.id)} className="w-full flex items-center gap-2 p-2 rounded hover:bg-white/5 text-left" data-testid={`chat-pick-${u.id}`}>
                      <UserAvatar src={u.profile_photo_url} fallback={(u.name || "?").slice(0,2)} className="w-8 h-8 rounded-full" />
                      <div className="flex-1">
                        <p className={`text-sm font-bold ${roleColor(u.role)}`}>{u.name}</p>
                        <p className="text-[10px] text-gray-500 uppercase">{u.role}</p>
                      </div>
                    </button>
                  </li>
                ))}
                {users.length === 0 && <li className="text-center text-xs text-gray-500 py-4">Sin resultados</li>}
              </ul>
            </>
          )}
          {tab === "club" && (
            <ul className="max-h-72 overflow-y-auto space-y-1">
              {!userClubId && <li className="text-center text-xs text-gray-500 py-4">No perteneces a ningún club</li>}
              {directivos.map((u) => (
                <li key={u.id}>
                  <button onClick={() => onPick(u.id)} className="w-full flex items-center gap-2 p-2 rounded hover:bg-white/5 text-left" data-testid={`chat-pick-directivo-${u.id}`}>
                    <UserAvatar src={u.profile_photo_url} fallback={(u.name || "?").slice(0,2)} className="w-8 h-8 rounded-full" />
                    <div className="flex-1">
                      <p className={`text-sm font-bold ${roleColor(u.role)}`}>{u.name}</p>
                      <p className="text-[10px] text-gray-500 uppercase">{u.role}</p>
                    </div>
                    <Buildings size={14} className="text-gray-500" />
                  </button>
                </li>
              ))}
              {userClubId && directivos.length === 0 && <li className="text-center text-xs text-gray-500 py-4">Sin directivos disponibles</li>}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
